/**
 * Filter Render for Desktop and Mobile Filters on Listpage.
 */

import $ from 'jquery';

import {prepareFilterUrl} from './FilterLogic.js'

import renderFilterChip from './templates/filter-chip.tpl.html';
import renderFilterItem from './templates/filter-item.tpl.html';
import renderFilterOverlay from './templates/filter-overlay.tpl.html';
import renderFilterCategory from './templates/filter-category.tpl.html';
import {objectDeepEquals} from "../search/Util";

const {EN, EN_KO, scrollLock} = window;

const getFilterGroup = ( identifier, filterJSON = null ) => {

	if ( null === filterJSON ) {
		filterJSON = window.filterJSON;
	}

	return filterJSON.find( ( group ) => {

		if ( 'dynamic' === group.identifier ) {
			return group.dynamic_identifier === identifier;
		}

		return group.identifier === identifier;
	} );
};

class AclFilterChip {

	alwaysRerender = false;

	isOpen = false;
	isSecondary = false;
	identifier = null;
	filterGroup = null;
	choiceUpdated = false;
	hasUpdate = false;
	isDefault = false;

	constructor( chipEl ) {
		this.chipEl = chipEl;
		this.chipEl.ACL_FILTER_CHIP ??= this;

		// Additional Args.
		this.isSecondary = this.chipEl.classList.contains( 'mdl_filter__item--secondary' );

		// Set Identifier.
		this.identifier = this.chipEl.dataset.identifier;

		// Open and Close Button Events.
		this.chipEl.addEventListener( 'click', ( evt ) => {

			// Open and Close Button Events.
			if ( evt.target.closest( '.mdl_filter__item-trigger' ) ) {
				return this.onButtonClick();
			}
			if ( evt.target.closest( '.mdl_filter__item-mobile-close' ) ) {
				return this.onCloseMobile();
			}

			// Submit and Reset Button Events.
			if ( evt.target.closest( 'button[type=submit]' ) ) {
				evt.preventDefault();
				return this.onSubmit()
			}

			if ( evt.target.closest( 'button[type=reset]' ) ) {
				evt.preventDefault();
				return this.onReset();
			}

			// Any input click we do not need to care about focus.
			if ( evt.target.closest( 'input, label, button' ) ) {
				return;
			}

			// If clicked inside set focus is nothing from above handles the click, necessary for esc keydown
			this.focus();
		} );

		// Set flag for choices change.
		this.chipEl.addEventListener( 'change', ( evt ) => {
			this.checkStatus();
		} );

		// Search Event.
		this.chipEl.addEventListener( 'input', ( evt ) => {
			if ( evt.target.matches( '.mdl_filter__item-search-input' ) ) {
				this.search( evt.target.value )
			}
		} );

		// Additional KeyEvents (just add it one time)
		document.addEventListener( 'keydown', ( evt ) => this.handleKeyDown( evt ) );

		EN.Media.watch( () => this.lockScroll( this.isOpen ), 'desktop' );
	}

	checkStatus() {
		this.hasUpdate = this.checkIfUpdate();
		this.isDefault = this.checkIfDefault();
		this.updateControls();
	}

	updateControls() {
		this.chipEl.querySelector( '.mdl_filter__item-control[type=submit]' ).toggleAttribute( 'disabled', !this.hasUpdate );
		this.chipEl.querySelector( '.mdl_filter__item-control[type=reset]' )?.toggleAttribute( 'disabled', this.isDefault );
		this.chipEl.querySelector( '.mdl_filter__item-mobile-reset[type=reset]' )?.classList.toggle( 'en_hidden', this.isDefault );
	}

	checkIfUpdate() {
		return this.getFilterInputsEls().some( inputEl => {
			switch( inputEl.type ) {
				case 'checkbox':
				case 'radio':
					return inputEl.checked !== ( inputEl.getAttribute( 'data-initial' ) === 'true' );
				case 'range':
					return inputEl.value !== inputEl.getAttribute( 'data-initial' );
			}
		} );
	}

	checkIfDefault() {
		return this.getFilterInputsEls().every( inputEl => {
			switch( inputEl.type ) {
				case 'checkbox':
				case 'radio':
					return inputEl.checked === ( inputEl.getAttribute( 'data-default' ) === 'true' );
				case 'range':
					return inputEl.value === inputEl.getAttribute( 'data-default' );
			}
		} );
	}

	getElement() {
		return this.chipEl;
	}

	getFieldset() {
		return this.chipEl.querySelector( 'fieldset' );
	}

	getIdentifier() {
		return this.identifier;
	}

	setOpen( isOpen ) {
		this.isOpen = isOpen;
		this.lockScroll( isOpen );

		this.chipEl.dispatchEvent( new CustomEvent( isOpen ? 'open_chip.acl' : 'close_chip.acl', { bubbles: true, detail: { chip: this } } ) );
	}

	isLocked = false;
	lockScroll( doLock = true ) {

		let contentEl = this.chipEl.querySelector( '.mdl_filter__item-content' );
		if ( contentEl ) {
			if ( doLock && EN.Media.matches( null, 'desktop' ) ) {
				if ( ! this.isLocked ) {

					// Enable scrollLock on Filter Element.
					scrollLock.disablePageScroll( contentEl );
					this.isLocked = true;
				}
			} else {
				if ( this.isLocked ) {
					scrollLock.enablePageScroll( contentEl );
					this.isLocked = false;
				}
			}
		}
	}

	open() {
		this.chipEl.classList.add( 'is-active' );
		setTimeout( () => {
			if ( this.isOpen ) {
				this.chipEl.classList.add( 'is-open' )
			}
		}, 1 );
		this.setOpen( true );
	}
	close() {
		// Submit the choices also when clicking outside of the dropdown.
		if ( this.hasUpdate ) {
			this.hasUpdate = false;
			this.submitChip( 'close' );
		}

		this.chipEl.classList.remove( 'is-open' );
		setTimeout( () => {
			if ( ! this.isOpen ) {
				this.chipEl.classList.remove( 'is-active' )
			}
		}, EN.Media.matches( null, 'desktop' ) ? 250 : 1 );
		this.setOpen( false );
	}

	openFromOverview() {
		this.open();
		this.focus();
	}

	closeFast() {
		this.chipEl.classList.remove( 'is-open', 'is-active' );
		this.setOpen( false );
	}

	toggle() {
		if ( this.isOpen ) {
			this.close();
		} else {
			this.open();
		}
	}

	focus() {
		this.chipEl.querySelector( '.mdl_filter__item-content .mdl_filter__field input:not([type=hidden])' )?.focus();
	}

	onButtonClick() {

		// Render with default data.
		this.renderChip().then( () => {

			// Open or close, the facet.
			this.toggle();
		} );
	}

	onCloseMobile() {
		this.undo();
		this.close();
	}

	getFilterGroup( identifier, filterJSON ) {
		return getFilterGroup( identifier, filterJSON );
	}

	lastMode = null;
	lastFilterJSON = null;

	async renderChip( customFilterJSON = null, mode = null ) {

		// FilterJSON.
		const filterJSON = customFilterJSON || window.filterJSON;

		// Do not rerender, if data did not change yet.
		if ( ! this.alwaysRerender ) {
			if ( this.lastFilterJSON && ( this.lastFilterJSON === filterJSON && this.lastMode === mode ) ) {
				return;
			}
		}

		// Fieldset-
		const fieldsetEl = this.getFieldset();

		// Get Identifier and FilterGroup to render.
		const identifier = this.getIdentifier();
		const filterGroup = this.getFilterGroup( identifier, filterJSON );

		// Render HTML into Fieldset.
		fieldsetEl.innerHTML = await EN.Render.render( 'filter-chip', {
			filterJSON,
			filterGroup,
		} );
		this.lastFilterJSON = filterJSON;
		this.lastMode = mode;
		this.filterGroup = structuredClone( filterGroup );

		const filterInputEls = this.getFilterInputsEls();

		filterInputEls.forEach( inputEl => {
			switch( inputEl.type ) {
				case 'checkbox':
				case 'radio':
					if( ! ( 'default' in inputEl.dataset ) ) {
						inputEl.dataset.default = false;
					}

					if( ! ( 'initial' in inputEl.dataset ) ) {
						inputEl.dataset.initial = inputEl.checked;
					}
					break;
				case 'range':
					if( ! ( 'initial' in inputEl.dataset ) ) {
						inputEl.dataset.initial = inputEl.value;
					}
					break;
			}
		} );

		this.checkStatus();

		// Fire event for custom integrations.
		this.chipEl.dispatchEvent( new CustomEvent( 'render_chip.acl', { detail: { chip: this }, bubbles: true } ) );
	}

	getFilterInputsEls() {
		const fieldsetEl = this.getFieldset();

		const permittedInputTypes = [ 'checkbox', 'radio', 'range'];
		return Array.from( fieldsetEl.elements ).filter( inputEl => inputEl.closest( '.mdl_filter__item-content' ) && inputEl.closest( '.mdl_filter__field' ) && permittedInputTypes.includes( inputEl.type ) );
	}

	search( searchInput ) {

		// Only works if there is a search input.
		const searchEl = this.chipEl.querySelector( '.mdl_filter__item-search-input' );
		if ( ! searchEl ) {
			return;
		}

		// Create string to LowerCase.
		const searchText = ''.concat( searchInput ).toLowerCase();

		const choiceEls = this.chipEl.querySelectorAll( '.mdl_filter__field-choice' );
		const messageEl = this.chipEl.querySelector( '.mdl_filter__item-choices--message' );

		// Get all choices, that match the string.
		const valids = Array.from( choiceEls ).map( choiceEl => {
			const label = choiceEl.querySelector( '.mdl_filter__field-choice-title' ).firstChild.textContent.toLowerCase();
			const value = choiceEl.querySelector( 'input' ).value.toLowerCase();
			const hasSearchInText = label.includes( searchText ) || value.includes( searchText );

			// Toggle display for all choices.
			choiceEl.classList.toggle( 'en_hidden', !hasSearchInText );

			return hasSearchInText;
		} ).filter( i => i );

		// Hide or show Message, if necessary.
		messageEl.classList.toggle( 'en_hidden', valids.length > 0 );
	}

	onSubmit() {
		if ( 'localStorage' in window ) {
			window.localStorage.setItem( 'scroll-to', '#products-filter' );
		}

		// Dispatch Submit to Controller.
		this.submitChip( 'submit' );
	}

	submitChip( action ) {
		const fieldset = this.getFieldset();
		this.chipEl.dispatchEvent( new CustomEvent( 'submit_chip.acl', { bubbles: true, detail: { chip: this, action, fieldset } } ) );
	}

	onReset() {
		// Reset contentInputsCurrent to default
		//this.checkForUpdate();

		// Clear inputs.
		this.reset();

		/// Update SearchInput.
		this.search( '' );

		// Dispatch Reset to Controller.
		this.chipEl.dispatchEvent( new CustomEvent( 'reset_chip.acl', { bubbles: true, detail: { chip: this } } ) );
	}

	handleKeyDown( evt ) {
		if( !this.isOpen ) return;

		switch ( evt.keyCode ) {
			// Escape
			case 27:
				evt.preventDefault();
				this.undo();
				this.close();
				break;
		}
	}

	reset() {
		//Empty search field
		const searchEl = this.chipEl.querySelector( '.mdl_filter__item-search-input' );
		if ( searchEl ) {
			searchEl.value = '';
			this.search( '' );
		}

		this.getFilterInputsEls().forEach( inputEl => {
			switch( inputEl.type ) {
				case 'checkbox':
				case 'radio':
					inputEl.checked = inputEl.getAttribute( 'data-default' ) === 'true';
					break;
				case 'range':
					inputEl.value = inputEl.getAttribute( 'data-default' );
					inputEl.dispatchEvent( new InputEvent( 'input' ) );
					break;
			}
		} );

		this.checkStatus();
	}

	undo() {
		//Empty search field
		const searchEl = this.chipEl.querySelector( '.mdl_filter__item-search-input' );
		if ( searchEl ) {
			searchEl.value = '';
			this.search( '' );
		}

		this.getFilterInputsEls().forEach( inputEl => {
			switch( inputEl.type ) {
				case 'checkbox':
				case 'radio':
					inputEl.checked = inputEl.getAttribute( 'data-initial' ) === 'true';
					break;
				case 'range':
					inputEl.value = inputEl.getAttribute( 'data-initial' );
					inputEl.dispatchEvent( new InputEvent( 'input' ) );
					break;
			}
		} );

		this.checkStatus();
	}
}

class AclCategoryChip extends AclFilterChip {

	alwaysRerender = true;

	constructor( chipEl ) {
		super( chipEl );

		this.chipEl.addEventListener( 'click', async( evt ) => {

			// Back Button Click.
			if ( evt.target.closest( '.js_filter__field-tree-item-back' ) ) {
				return this.onBackButton();
			}

			// Open next Category.
			if ( evt.target.closest( '.mdl_filter__field-tree-item--has-subcat' ) ) {

				const subTree = evt.target.closest( '.mdl_filter__field-tree-sub' );

				// Only execute, if this is the lowest category on the tree, or the root.
				if ( subTree && subTree.nextElementSibling ) {
					return;
				}

				const categoryId = evt.target.closest( '.mdl_filter__field-tree-item' ).dataset.menu;
				await this.openCategory( categoryId );
			}
		} );

		const events = [
			'transitionend',
			'webkitTransitionEnd',
			'oTransitionEnd',
		];
		events.forEach( eventName => {
			this.chipEl.addEventListener( eventName, ( evt ) => {
				if ( evt.target.closest( '.mdl_filter__field-tree-sub' ) ) {
					if ( evt.target.classList.contains( 'mdl_filter__field-tree-sub' ) ) {
						this.onTransitionEnd( evt.target );
					}
				}
			} )
		} )
	}

	findCategory( categoryId, filterGroup ) {

		// Reducer to find the correct category.
		const findCategoryReducer = ( found,current ) => {
			if ( found ) {
				return found;
			}

			if ( current.id === categoryId ) {
				return current;
			}

			return current.children.reduce( findCategoryReducer, null );
		};

		return Object.values( filterGroup.filter[0].categories ).reduce( findCategoryReducer, null );
	}

	async openCategory( categoryId ) {

		const identifier = this.getIdentifier();

		const filterJSON = this.lastFilterJSON;
		const filterGroup = getFilterGroup( identifier, filterJSON );
		const category = this.findCategory( categoryId, filterGroup );

		const renderedHTML = await EN.Render.render( 'filter-category', {
			filterGroup,
			category,
		} );

		const container = this.chipEl.querySelector( '.mdl_filter__field-tree' );

		const subTree = document.createElement( 'div' );
		subTree.className = 'mdl_filter__field-tree-sub';
		subTree.innerHTML = renderedHTML;

		container.appendChild( subTree );

		const filterInputEls = this.getFilterInputsEls();

		filterInputEls.forEach( inputEl => {
			inputEl.dataset.default = inputEl.checked;
			inputEl.dataset.initial = inputEl.checked;
		} );

		this.checkStatus();

		// So the is-visible Animation gets triggered correctly.
		requestAnimationFrame( () => {

			subTree.classList.add( 'is-visible' );

			// Fallback Timeout, in case the Transition Event Listener, doesn't trigger.
			setTimeout( () => {
				this.onTransitionEnd( subTree );
			}, 500 );
		} )
	}

	onBackButton() {

		const container = this.chipEl.querySelector( '.mdl_filter__field-tree' );
		const subTrees = Array.from( container.querySelectorAll( '.mdl_filter__field-tree-sub' ) );

		if ( subTrees.length <= 0 ) {
			return;
		}

		// Remove last subTree.
		const oldSubTree = subTrees.pop();
		oldSubTree.classList.remove( 'is-visible' );

		// Show next underlying Container.
		if ( subTrees.length <= 0 ) {
			container.classList.remove( 'is-subcat-open' );
		} else {
			subTrees[ subTrees.length - 1 ].classList.remove( 'en_hidden' );
		}

		// Fallback Timeout, in case the Transition Event Listener, doesn't trigger.
		setTimeout( () => {
			this.onTransitionEnd( oldSubTree );
		}, 500 );
	}

	onTransitionEnd( targetEl ) {

		if ( targetEl.classList.contains( 'is-visible' ) ) {
			const containerEl = this.chipEl.querySelector( '.mdl_filter__field-tree' );
			const subTreeEls = Array.from( containerEl.querySelectorAll( '.mdl_filter__field-tree-sub' ) );

			// Hide all underlying containers.
			containerEl.classList.add( 'is-subcat-open' );
			subTreeEls.forEach( ( subTreeEl ) => {
				if ( subTreeEl !== targetEl ) {
					subTreeEl.classList.add( 'en_hidden' );
				}
			} );

		} else {
			if ( targetEl.isConnected ) {
				targetEl.remove();
			}
		}
	}
}


class AclFilterOverview {

	filterJSON = null;
	isLoading = false;

	constructor( overviewEl ) {
		this.overviewEl = overviewEl;
		this.overviewEl.ACL_FILTER_OVERVIEW ??= this;

		// Global click events.
		document.addEventListener( 'click', ( ev ) => {
			// Show Overlay Button Event.
			if ( ev.target.closest( '.js_filter__item-trigger-all-filter' ) ) {
				ev.preventDefault();
				this.openOverview()
			}

			// Hide Overlay Button Event.
			if ( ev.target.closest( '.js_filter__overview-close' )  ) {
				ev.preventDefault();
				this.closeOverview();
			}
		} );

		// Clicks inside the overviewEl.
		this.overviewEl.addEventListener( 'click', ( ev ) => {
			const targetEl = ev.target;

			const submitButtonEl = targetEl.closest( '.mdl_filter__overview-submit' );
			if ( submitButtonEl ) {

				ev.preventDefault();
				if ( submitButtonEl.disabled ) {
					return;
				}

				this.onSubmit();
			} else if ( targetEl.closest( '.mdl_filter__overview-reset' ) ) {

				ev.preventDefault();

				this.onReset();
			} else {

				// Click on any chip.
				const triggerEl = targetEl.closest( '.js_filter__overview-filter-item-trigger' );
				if ( triggerEl ) {

					const identifier = triggerEl.parentElement.dataset.identifier;
					this.openChip( identifier );
				}
			}
		} );

		// Additional KeyEvents.
		document.addEventListener( 'keydown', ( evt ) => this.handleKeyDown( evt ) );

		EN.Media.watch( ( mq ) => {
			if ( mq.matches && this.isOpen ) {
				this.closeOverview();
			}
		}, 'desktop' );
	}

	handleKeyDown( evt ) {
		switch ( evt.keyCode ) {
			// Escape
			case 27:
				evt.preventDefault();
				if( AclFilterController.isOpenFilterChip ) {
					return;
				}
				this.closeOverview();
				break;
		}
	}

	getController() {
		const formEl = this.overviewEl.closest( '.mdl_filter__form' );
		return formEl.ACL_FILTER_CONTROLLER;
	}

	setLoading( loading = true ) {
		this.isLoading = loading;
		this.overviewEl.classList.toggle( 'is-loading', this.isLoading );
	}

	isOpen = false;
	setOpen( isOpen = true ) {
		this.isOpen = isOpen;
		this.lockScroll( isOpen );
	}

	isLocked = false;
	lockScroll( doLock = true ) {

		const contentEl = this.overviewEl.querySelector( '.mdl_filter__overview-content' );
		if ( contentEl ) {
			if ( doLock && EN.Media.matches( null, 'desktop' ) ) {
				if ( ! this.isLocked ) {
					scrollLock.disablePageScroll( contentEl );
					this.isLocked = true;
				}
			} else {
				if ( this.isLocked ) {
					scrollLock.enablePageScroll( contentEl );
					this.isLocked = false;
				}
			}
		}
	}

	openOverview() {
		if ( window.filterJSON ) {
			this.filterJSON = window.filterJSON;
		}
		this.renderOverview( window.location.pathname ).then( () => {
			this.overviewEl.classList.add( 'is-active' );
			setTimeout( () => this.overviewEl.classList.add( 'is-open' ), 1 );
			this.setOpen( true );
		} );
	}

	closeOverview() {
		this.overviewEl.classList.remove( 'is-open' );
		setTimeout( () => this.overviewEl.classList.remove( 'is-active' ), 250 );

		this.setOpen( false );
	}

	openChip( identifier ) {
		const Controller = this.getController();
		const chip = Controller.getChip( identifier ) || Controller.addHiddenChip( identifier, this.filterJSON );
		if ( chip ) {
			return chip.renderChip( this.filterJSON, 'overview' ).then( () => {
				chip.openFromOverview();
			} )
		}
	}

	updateData( newUrl ) {

		const fetchUrl = new URL( newUrl, document.baseURI );
		fetchUrl.searchParams.append( 'filterJSON', 1 );

		this.setLoading( true );
		return fetch( fetchUrl.toString() )
			.then( ( res ) => res.json() )
			.then( ( res ) => {
				this.filterJSON = res;
				return this.renderOverview( newUrl, res );
			} )
			.finally( () => this.setLoading( false ) );
	}

	async renderOverview( newUrl, data ) {

		const filterGroups = data || this.filterJSON;

		// Render Overview HTML and append content.
		const filterContainerEl = this.overviewEl.querySelector( '.mdl_filter__overview-filters' );
		filterContainerEl.innerHTML = await EN.Render.render( 'filter-overlay', {
			filterGroups
		} );

		// Update the Button.
		const filters = filterGroups.find( ( filterGroup ) => filterGroup.identifier === 'salenew' )?.filter || [];

		// Sum of all Sale and non-sale articles.
		const articleCount = this.articleCount || filters.reduce( ( prev, filter ) => {
			if ( 'sale' === filter.name ) {
				return prev + filter.count;
			}
			return prev;
		}, 0 );

		// Disabled Button shows different content.
		const isDisabled = newUrl === window.location.pathname;

		// Set Submit Button
		const submitButtonEl = this.overviewEl.querySelector( '.mdl_filter__overview-submit' );
		submitButtonEl.dataset.href = newUrl;
		submitButtonEl.disabled = isDisabled;
		submitButtonEl.innerHTML = isDisabled ? "Speichern" : "Zeige " + EN_KO.functions.format_number( articleCount ) + " Artikel";

		return true;
	}

	getUrl() {
		const submitButtonEl = this.overviewEl.querySelector( '.mdl_filter__overview-submit' );
		return submitButtonEl.dataset.href;
	}

	onSubmit() {
		const newUrl = this.getUrl();
		if ( newUrl ) {
			window.location.href = newUrl;
		}
	}

	onReset() {
		// Need to call from Controller.
		const Controller = this.getController();

		// Reset all chips.
		Controller.getChips().forEach( chip => chip.reset() );

		// Then submit the whole form.
		Controller.submit( Controller.formEl );
	}
}

class AclFilterController {
	static isOpenFilterChip = false;

	constructor( formEl ) {
		this.formEl = formEl;
		this.formEl.ACL_FILTER_CONTROLLER ??= this;

		// Setup Chips.
		this.getChips();

		// Setup Overview.
		if ( ! this.formEl.classList.contains( 'mdl_filter__form--factfinder' ) ) {
			this.getOverview();
		}

		// Disable normal Form Events.
		this.formEl.addEventListener( 'submit', ( ev ) => ev.preventDefault() );

		// Show More Button Event.
		this.formEl.addEventListener( 'click', ( ev ) => {
			if ( ev.target.closest( '.js_filter__item-trigger-show-more-filter' ) ) {
				this.showAll();
			}
		} )

		// Listen to submit of a single chip.
		this.formEl.addEventListener( 'submit_chip.acl', ( ev ) => {
			this.submit( ev.detail.fieldset );

			// Close the chip, if the overlay is open.
			if ( 'submit' === ev.detail.action && this.getOverview()?.isOpen ) {
				ev.detail.chip?.close();
			}
		} )

		// this.formEl.addEventListener( 'reset', ( ev ) => ev.preventDefault() );

		// Close other chips, when a chip opens.
		this.formEl.addEventListener( 'open_chip.acl', ( ev ) => {
			AclFilterController.isOpenFilterChip = true;
			// Close all other chips, when we open a new one.
			this.closeChips( ev.detail.chip );
		} );

		// Close other chips, when a chip opens.
		this.formEl.addEventListener( 'close_chip.acl', ( ev ) => {
			AclFilterController.isOpenFilterChip = false;
		} );

		// Close filter if clicked outside
		document.addEventListener( EN_KO.settings.event, ( ev ) => {

			// Skip when we just triggered the opening.
			const target = ev.target;
			if ( target.closest( '.js_filter__overview-filter-item-trigger' ) ) {
				return;
			}

			// Check all open chips and close, if necessary.
			const chips = this.getChips().filter( chip => chip.isOpen );
			chips.forEach( chip => {
				if ( ! chip.getElement().contains( ev.target ) ) {
					chip.close();
				}
			} )
		} );
	}

	getOverview() {
		// Setup Overview.
		const overviewEl = this.formEl.querySelector( '.mdl_filter__overview' );
		if ( overviewEl ) {
			overviewEl.ACL_FILTER_OVERVIEW ??= new AclFilterOverview( overviewEl );
			return overviewEl.ACL_FILTER_OVERVIEW;
		}

		return null;
	}

	createChip( chipEl ) {
		if ( chipEl.classList.contains( 'mdl_filter__item--tree' ) ) {
			chipEl.ACL_FILTER_CHIP ??= new AclCategoryChip( chipEl );
		} else {
			chipEl.ACL_FILTER_CHIP ??= new AclFilterChip( chipEl );
		}
		return chipEl.ACL_FILTER_CHIP;
	}

	getChips() {
		const itemEls = this.formEl.querySelectorAll( '.mdl_filter__item[data-identifier]' );
		return Array.from( itemEls ).map( ( chipEl ) => this.createChip( chipEl ) );
	}

	getChip( identifier ) {
		return this.getChips().find( chip => chip.getIdentifier() === identifier );
	}

	addHiddenChip( identifier, filterJSON ) {
		const filterGroup = getFilterGroup( identifier, filterJSON );

		const chipsClasses = [ 'mdl_filter__item' ];
		chipsClasses.push( 'mdl_filter__item--hidden_in_list' );

		if ( filterGroup.variant ) {
			chipsClasses.push( `mdl_filter__item--${filterGroup.variant}` );
		}

		if ( filterGroup.multi ) {
			chipsClasses.push( 'mdl_filter__item--nodefaultmultiselect' );
		}

		const chipEl = document.createElement( 'li' );
		chipEl.classList.add( ...chipsClasses );
		chipEl.innerHTML = '<fieldset class="mdl_filter__item-container"></fieldset>';
		chipEl.dataset.identifier = identifier;

		const Chips = this.getChips();
		const lastChipEl = Chips[ Chips.length - 1 ]?.getElement();
		if ( lastChipEl ) {
			lastChipEl.insertAdjacentElement( 'afterend', chipEl );
		} else {
			this.formEl.querySelector( '.mdl_filter__list' ).append( chipEl );
		}

		return this.createChip( chipEl );
	}

	closeChips( except = null ) {

		// Close all open chips except the current one.
		this.getChips().filter( chip => chip !== except ).filter( chip => chip.isOpen ).forEach( chip => chip.closeFast() );
	}

	showAll() {
		this.formEl.classList.add( 'mdl_filter__form--show-all-filters' );
	}

	submit( submitEl ) {

		const data = $( submitEl ).serializeJSON();

		const Overview = this.getOverview();
		if ( Overview?.isOpen ) {
			const newUrl = prepareFilterUrl( data, Overview.getUrl() );
			Overview.updateData( newUrl );
		} else {
			const newUrl = prepareFilterUrl( data );
			window.location.href = newUrl;
		}
	}
}


$( document ).ready( function() {

	// Add Templates to Renderer.
	EN.Render.addTemplate( 'filter-chip', renderFilterChip );
	EN.Render.addTemplate( 'filter-item', renderFilterItem );
	EN.Render.addTemplate( 'filter-category', renderFilterCategory );
	EN.Render.addTemplate( 'filter-overlay', renderFilterOverlay );

	const formEls = document.querySelectorAll( '.mdl_filter__form' );
	if ( formEls ) {
		formEls.forEach( formEl => {
			formEl.ACL_FILTER_CONTROLLER ??= new AclFilterController( formEl );
		} );
	}
} );

export {
	AclFilterController,
	AclFilterChip,
	AclCategoryChip,
	AclFilterOverview,
};
